import React, { Dispatch, SetStateAction } from "react";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import { FormControl, MenuItem, Select } from "@mui/material";

type props = {
  module: any;
  page: number;
  onPageChange: (newPage: number) => void;
  totalPages: number;
  totalCount: number;
  setPage: Dispatch<SetStateAction<number>>;
  limit?: number;
};

export default function Pagination({
  module,
  page,
  onPageChange,
  totalPages,
  setPage,
  totalCount,
  limit = 10,
}: props) {
  const startIndex = (page - 1) * limit + 1;
  const endIndex = Math.min(startIndex + limit - 1, totalCount);
  
  // Primary color from theme
  const primaryColor = '#192952';
  const primaryHover = '#2a3d6b';
  const primaryLight = '#B9D8EB';
  
  return (
    <div className="pagination_wrp" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      <div className="pagination_number" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        {totalPages > 5 ? (
          <FormControl className="all_page" sx={{ minWidth: 80 }}>
            <Select
              className="dropse"
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              inputProps={{
                "aria-label": "Without label",
              }}
              displayEmpty
              value={page}
              onChange={(pages: any) => setPage(pages.target.value)}
              sx={{
                height: '40px',
                fontSize: '14px',
                borderRadius: '8px',
                border: `1px solid ${primaryColor}20`,
                '& .MuiSelect-select': {
                  padding: '8px 32px 8px 12px',
                  color: primaryColor,
                  fontWeight: '500',
                },
                '&:hover': {
                  borderColor: primaryColor,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: `${primaryColor}20`,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: primaryColor,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: primaryColor,
                }
              }}
            >
              <MenuItem value="" disabled>
                Select
              </MenuItem>
              {Array.from({ length: totalPages }).map((_, index) => (
                <MenuItem key={index + 1} value={index + 1}>
                  {index + 1}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : null}
        <button
          className="page_icon first_icon"
          onClick={() => onPageChange(1)}
          disabled={page === 1}
          style={{
            minWidth: '40px',
            height: '40px',
            padding: '0',
            border: `1px solid ${page === 1 ? '#e0e0e0' : `${primaryColor}30`}`,
            borderRadius: '8px',
            background: page === 1 ? '#f5f5f5' : '#fff',
            cursor: page === 1 ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            boxShadow: page === 1 ? 'none' : '0 2px 4px rgba(0,0,0,0.05)',
          }}
          onMouseEnter={(e) => {
            if (page !== 1) {
              e.currentTarget.style.background = primaryLight;
              e.currentTarget.style.borderColor = primaryColor;
              e.currentTarget.style.boxShadow = '0 4px 8px rgba(25, 41, 82, 0.15)';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }
          }}
          onMouseLeave={(e) => {
            if (page !== 1) {
              e.currentTarget.style.background = '#fff';
              e.currentTarget.style.borderColor = `${primaryColor}30`;
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
              e.currentTarget.style.transform = 'translateY(0)';
            }
          }}
        >
          <KeyboardDoubleArrowLeftIcon className="mat_icon" style={{ fontSize: '20px', color: page === 1 ? '#ccc' : primaryColor }} />
        </button>
        <button
          className="page_icon"
          onClick={() => onPageChange(page - 1)}
          disabled={page === 1}
          style={{
            minWidth: '40px',
            height: '40px',
            padding: '0',
            border: `1px solid ${page === 1 ? '#e0e0e0' : `${primaryColor}30`}`,
            borderRadius: '8px',
            background: page === 1 ? '#f5f5f5' : '#fff',
            cursor: page === 1 ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            boxShadow: page === 1 ? 'none' : '0 2px 4px rgba(0,0,0,0.05)',
          }}
          onMouseEnter={(e) => {
            if (page !== 1) {
              e.currentTarget.style.background = primaryLight;
              e.currentTarget.style.borderColor = primaryColor;
              e.currentTarget.style.boxShadow = '0 4px 8px rgba(25, 41, 82, 0.15)';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }
          }}
          onMouseLeave={(e) => {
            if (page !== 1) {
              e.currentTarget.style.background = '#fff';
              e.currentTarget.style.borderColor = `${primaryColor}30`;
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
              e.currentTarget.style.transform = 'translateY(0)';
            }
          }}
        >
          <span style={{ fontSize: '20px', fontWeight: '600', color: page === 1 ? '#ccc' : primaryColor }}>‹</span>
        </button>
        <ul className="pagination_list" style={{ display: 'flex', listStyle: 'none', margin: 0, padding: 0, gap: '6px', alignItems: 'center' }}>
          {totalPages > 4
            ? Array.from({ length: totalPages })
              .slice(0, 3)
              .map((_, index) => (
                <li key={index} className={page === index + 1 ? "active" : ""} style={{ margin: 0 }}>
                  <button
                    type="button"
                    onClick={() => onPageChange(index + 1)}
                    disabled={page === index + 1}
                    aria-current={page === index + 1 ? "page" : undefined}
                    style={{
                      minWidth: '40px',
                      height: '40px',
                      padding: '0',
                      border: `1px solid ${page === index + 1 ? primaryColor : `${primaryColor}30`}`,
                      borderRadius: '8px',
                      background: page === index + 1 ? primaryColor : '#fff',
                      color: page === index + 1 ? '#fff' : primaryColor,
                      cursor: page === index + 1 ? 'default' : 'pointer',
                      fontSize: '14px',
                      fontWeight: page === index + 1 ? '600' : '500',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxShadow: page === index + 1 ? '0 4px 12px rgba(25, 41, 82, 0.25)' : '0 2px 4px rgba(0,0,0,0.05)',
                    }}
                    onMouseEnter={(e) => {
                      if (page !== index + 1) {
                        e.currentTarget.style.background = primaryLight;
                        e.currentTarget.style.borderColor = primaryColor;
                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(25, 41, 82, 0.15)';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (page !== index + 1) {
                        e.currentTarget.style.background = '#fff';
                        e.currentTarget.style.borderColor = `${primaryColor}30`;
                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }
                    }}
                  >
                    {index + 1}
                  </button>
                </li>
              ))
              .concat(
                <li key="ellipsis" className="ellipsis" style={{ margin: 0, padding: '0 8px' }}>
                  <span aria-hidden="true" style={{ fontSize: '20px', color: primaryColor, fontWeight: '600' }}>…</span>
                </li>,
                <li
                  key="middle"
                  style={{
                    display: page < 4 || page === totalPages ? "none" : undefined,
                    margin: 0,
                  }}
                  className={page === totalPages ? "active" : ""}
                >
                  <button 
                    type="button" 
                    disabled
                    style={{
                      minWidth: '40px',
                      height: '40px',
                      padding: '0',
                      border: `1px solid ${primaryColor}`,
                      borderRadius: '8px',
                      background: primaryColor,
                      color: '#fff',
                      cursor: 'default',
                      fontSize: '14px',
                      fontWeight: '600',
                      boxShadow: '0 4px 12px rgba(25, 41, 82, 0.25)',
                    }}
                  >
                    {page}
                  </button>
                </li>,
                <li
                  key={totalPages}
                  className={page === totalPages ? "active" : "inActv"}
                  style={{ margin: 0 }}
                >
                  <button
                    type="button"
                    onClick={() => onPageChange(totalPages)}
                    disabled={page === totalPages}
                    aria-current={page === totalPages ? "page" : undefined}
                    style={{
                      minWidth: '40px',
                      height: '40px',
                      padding: '0',
                      border: `1px solid ${page === totalPages ? primaryColor : `${primaryColor}30`}`,
                      borderRadius: '8px',
                      background: page === totalPages ? primaryColor : '#fff',
                      color: page === totalPages ? '#fff' : primaryColor,
                      cursor: page === totalPages ? 'default' : 'pointer',
                      fontSize: '14px',
                      fontWeight: page === totalPages ? '600' : '500',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxShadow: page === totalPages ? '0 4px 12px rgba(25, 41, 82, 0.25)' : '0 2px 4px rgba(0,0,0,0.05)',
                    }}
                    onMouseEnter={(e) => {
                      if (page !== totalPages) {
                        e.currentTarget.style.background = primaryLight;
                        e.currentTarget.style.borderColor = primaryColor;
                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(25, 41, 82, 0.15)';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (page !== totalPages) {
                        e.currentTarget.style.background = '#fff';
                        e.currentTarget.style.borderColor = `${primaryColor}30`;
                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }
                    }}
                  >
                    {totalPages}
                  </button>
                </li>
              )
            : Array.from({ length: totalPages }).map((_, index) => (
              <li
                key={index}
                className={page === index + 1 ? "active" : "inActv"}
                style={{ margin: 0 }}
              >
                <button
                  type="button"
                  onClick={() => onPageChange(index + 1)}
                  disabled={page === index + 1}
                  aria-current={page === index + 1 ? "page" : undefined}
                  style={{
                    minWidth: '40px',
                    height: '40px',
                    padding: '0',
                    border: `1px solid ${page === index + 1 ? primaryColor : `${primaryColor}30`}`,
                    borderRadius: '8px',
                    background: page === index + 1 ? primaryColor : '#fff',
                    color: page === index + 1 ? '#fff' : primaryColor,
                    cursor: page === index + 1 ? 'default' : 'pointer',
                    fontSize: '14px',
                    fontWeight: page === index + 1 ? '600' : '500',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: page === index + 1 ? '0 4px 12px rgba(25, 41, 82, 0.25)' : '0 2px 4px rgba(0,0,0,0.05)',
                  }}
                  onMouseEnter={(e) => {
                    if (page !== index + 1) {
                      e.currentTarget.style.background = primaryLight;
                      e.currentTarget.style.borderColor = primaryColor;
                      e.currentTarget.style.boxShadow = '0 4px 8px rgba(25, 41, 82, 0.15)';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (page !== index + 1) {
                      e.currentTarget.style.background = '#fff';
                      e.currentTarget.style.borderColor = `${primaryColor}30`;
                      e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  {index + 1}
                </button>
              </li>
            ))}
        </ul>

        {/* <ul className="pagination_list">
          <li className="active">1</li>
          <li>2</li>
          <li>3</li>
          <li>4</li>
        </ul> */}
        <button
          className="page_icon"
          onClick={() => onPageChange(page + 1)}
          disabled={page === totalPages}
          style={{
            minWidth: '40px',
            height: '40px',
            padding: '0',
            border: `1px solid ${page === totalPages ? '#e0e0e0' : `${primaryColor}30`}`,
            borderRadius: '8px',
            background: page === totalPages ? '#f5f5f5' : '#fff',
            cursor: page === totalPages ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            boxShadow: page === totalPages ? 'none' : '0 2px 4px rgba(0,0,0,0.05)',
          }}
          onMouseEnter={(e) => {
            if (page !== totalPages) {
              e.currentTarget.style.background = primaryLight;
              e.currentTarget.style.borderColor = primaryColor;
              e.currentTarget.style.boxShadow = '0 4px 8px rgba(25, 41, 82, 0.15)';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }
          }}
          onMouseLeave={(e) => {
            if (page !== totalPages) {
              e.currentTarget.style.background = '#fff';
              e.currentTarget.style.borderColor = `${primaryColor}30`;
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
              e.currentTarget.style.transform = 'translateY(0)';
            }
          }}
        >
          <span style={{ fontSize: '20px', fontWeight: '600', color: page === totalPages ? '#ccc' : primaryColor }}>›</span>
        </button>
        <button
          className="page_icon last_icon"
          onClick={() => onPageChange(totalPages)}
          disabled={page === totalPages}
          style={{
            minWidth: '40px',
            height: '40px',
            padding: '0',
            border: `1px solid ${page === totalPages ? '#e0e0e0' : `${primaryColor}30`}`,
            borderRadius: '8px',
            background: page === totalPages ? '#f5f5f5' : '#fff',
            cursor: page === totalPages ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            boxShadow: page === totalPages ? 'none' : '0 2px 4px rgba(0,0,0,0.05)',
          }}
          onMouseEnter={(e) => {
            if (page !== totalPages) {
              e.currentTarget.style.background = primaryLight;
              e.currentTarget.style.borderColor = primaryColor;
              e.currentTarget.style.boxShadow = '0 4px 8px rgba(25, 41, 82, 0.15)';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }
          }}
          onMouseLeave={(e) => {
            if (page !== totalPages) {
              e.currentTarget.style.background = '#fff';
              e.currentTarget.style.borderColor = `${primaryColor}30`;
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
              e.currentTarget.style.transform = 'translateY(0)';
            }
          }}
        >
          <KeyboardDoubleArrowRightIcon className="mat_icon" style={{ fontSize: '20px', color: page === totalPages ? '#ccc' : primaryColor }} />
        </button>
      </div>
    </div>
  );
}
